/// <reference path="../../../../../node_modules/zone.js/dist/zone.js.d.ts" />

// original code is from @angular/core/zone/ng_zone.ts
/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */

import { EventEmitter, NgZone } from '@angular/core';

export interface ZoneSyncChild {
  // Zone for a child window
  Zone: ZoneType;

  inner: Zone;
  outer: Zone;
}

// 同期用データ
export interface ZoneSyncData {
  zones: Array<ZoneSyncChild>;
  zone: NgZonePrivate;
}

declare global {
  interface Window {
    zoneSync: ZoneSyncData;
  }
}
export function runAllInAngularZone<T>(data: ZoneSyncData, fn: (...args: any[]) => T): T {
  function runInRecursive(index: number): T {
    for (; index < data.zones.length; index++) {
      if (!data.zones[index].Zone.current.get('isAngularZone')) {
        return data.zones[index].inner.run(runInRecursive, undefined, [index + 1]);
      }
    }
    return fn();
  }
  return runInRecursive(0);
}
export function runOutsideAllInAngularZone<T>(data: ZoneSyncData, fn: (...args: any[]) => T): T {
  function runInRecursive(index: number): T {
    for (; index < data.zones.length; index++) {
      if (data.zones[index].Zone.current.get('isAngularZone')) {
        return data.zones[index].outer.run(runInRecursive, undefined, [index + 1]);
      }
    }
    return fn();
  }
  return runInRecursive(0);
}

export class NgZoneForParentWindow implements NgZone {
  readonly hasPendingMacrotasks: boolean = false;
  readonly hasPendingMicrotasks: boolean = false;

  /**
   * Whether there are no outstanding microtasks or macrotasks.
   */
  readonly isStable: boolean = true;

  /**
   * Notifies when code enters Angular Zone. This gets fired first on VM Turn.
   */
  readonly onUnstable: EventEmitter<any> = new EventEmitter(false);

  /**
   * Notifies when there is no more microtasks enqueued in the current VM Turn.
   * This is a hint for Angular to do change detection, which may enqueue more microtasks.
   * For this reason this event can fire multiple times per VM Turn.
   */
  readonly onMicrotaskEmpty: EventEmitter<any> = new EventEmitter(false);

  /**
   * Notifies when the last `onMicrotaskEmpty` has run and there are no more microtasks, which
   * implies we are about to relinquish VM turn.
   * This event gets called just once.
   */
  readonly onStable: EventEmitter<any> = new EventEmitter(false);

  /**
   * Notifies that an error has been delivered.
   */
  readonly onError: EventEmitter<any> = new EventEmitter(false);

  constructor({ enableLongStackTrace = false }) {
    if (typeof Zone === 'undefined') {
      throw new Error(`In this configuration Angular requires Zone.js`);
    }

    Zone.assertZonePatched();
    const self = (this as any) as NgZonePrivate;
    self._nesting = 0;
    self._countPendingMacrotasks = 0;
    self._countPendingMicrotasks = 0;

    self._outer = self._inner = Zone.current;

    if ((Zone as any)['wtfZoneSpec']) {
      self._inner = self._inner.fork((Zone as any)['wtfZoneSpec']);
    }

    if ((Zone as any)['TaskTrackingZoneSpec']) {
      self._inner = self._inner.fork(new ((Zone as any)['TaskTrackingZoneSpec'] as any)());
    }

    if (enableLongStackTrace && (Zone as any)['longStackTraceZoneSpec']) {
      self._inner = self._inner.fork((Zone as any)['longStackTraceZoneSpec']);
    }

    forkInnerZoneWithAngularBehavior(self);
    const zoneSync = {
      zones: [
        {
          inner: self._inner,
          outer: self._outer,
          Zone,
        },
      ],
      zone: self,
    };
    window.zoneSync = zoneSync;
  }

  run<T>(fn: (...args: any[]) => T, applyThis?: any, applyArgs?: any[]): T {
    return ((this as any) as NgZonePrivate)._inner.run(fn, applyThis, applyArgs) as T;
  }

  runTask<T>(fn: (...args: any[]) => T, applyThis?: any, applyArgs?: any[], name?: string): T {
    const zone = ((this as any) as NgZonePrivate)._inner;
    const task = zone.scheduleEventTask('NgZoneEvent: ' + name, fn, EMPTY_PAYLOAD, noop, noop);
    try {
      return zone.runTask(task, applyThis, applyArgs) as T;
    } finally {
      zone.cancelTask(task);
    }
  }

  runGuarded<T>(fn: (...args: any[]) => T, applyThis?: any, applyArgs?: any[]): T {
    return ((this as any) as NgZonePrivate)._inner.runGuarded(fn, applyThis, applyArgs) as T;
  }

  runOutsideAngular<T>(fn: (...args: any[]) => T): T {
    return runOutsideAllInAngularZone(window.zoneSync, fn);
  }
}

function noop() {}
const EMPTY_PAYLOAD = {};

export interface NgZonePrivate extends NgZoneForParentWindow {
  _outer: Zone;
  _inner: Zone;
  _nesting: number;
  _countPendingMicrotasks: number;
  _countPendingMacrotasks: number;

  hasPendingMacrotasks: boolean;
  hasPendingMicrotasks: boolean;
  isStable: boolean;
}

export function checkStable(zone: NgZonePrivate) {
  if (zone._nesting === 0 && !zone.hasPendingMicrotasks && !zone.isStable) {
    try {
      zone._nesting++;
      zone.onMicrotaskEmpty.emit(null);
    } finally {
      zone._nesting--;
      if (!zone.hasPendingMicrotasks) {
        try {
          zone.runOutsideAngular(() => zone.onStable.emit(null));
        } finally {
          zone.isStable = true;
        }
      }
    }
  }
}

function forkInnerZoneWithAngularBehavior(zone: NgZonePrivate) {
  let countMicroTask = false;
  let countMacroTask = false;

  zone._inner = zone._inner.fork({
    name: 'angular',
    properties: <any>{ isAngularZone: true, maybeDelayChangeDetection: false },
    onInvokeTask: (delegate: ZoneDelegate, current: Zone, target: Zone, task: Task, applyThis: any, applyArgs: any): any => {
      return runAllInAngularZone(window.zoneSync, () => {
        try {
          onEnter(zone);
          return delegate.invokeTask(target, task, applyThis, applyArgs);
        } finally {
          onLeave(zone);
        }
      });
    },

    onInvoke: (
      delegate: ZoneDelegate,
      current: Zone,
      target: Zone,
      callback: Function,
      applyThis: any,
      applyArgs?: any[],
      source?: string
    ): any => {
      return runAllInAngularZone(window.zoneSync, () => {
        try {
          onEnter(zone);
          return delegate.invoke(target, callback, applyThis, applyArgs, source);
        } finally {
          onLeave(zone);
        }
      });
    },

    onHasTask: (delegate: ZoneDelegate, current: Zone, target: Zone, hasTaskState: HasTaskState) => {
      delegate.hasTask(target, hasTaskState);
      if (current === target) {
        // We are only interested in hasTask events which originate from our zone
        // (A child hasTask event is not interesting to us)
        if (hasTaskState.change === 'microTask') {
          if (countMicroTask !== hasTaskState.microTask) {
            if (hasTaskState.microTask) {
              zone._countPendingMicrotasks++;
            } else {
              zone._countPendingMicrotasks--;
            }
          }
          updateMicroTaskStatus(zone);
          checkStable(zone);
          countMicroTask = hasTaskState.microTask;
        } else if (hasTaskState.change === 'macroTask') {
          if (countMacroTask !== hasTaskState.macroTask) {
            if (hasTaskState.macroTask) {
              zone._countPendingMacrotasks++;
            } else {
              zone._countPendingMacrotasks--;
            }
          }
          zone.hasPendingMacrotasks = zone._countPendingMacrotasks > 0;
          countMacroTask = hasTaskState.macroTask;
        }
      }
    },

    onHandleError: (delegate: ZoneDelegate, current: Zone, target: Zone, error: any): boolean => {
      delegate.handleError(target, error);
      zone.runOutsideAngular(() => zone.onError.emit(error));
      return false;
    },
  });
}

export function updateMicroTaskStatus(zone: NgZonePrivate) {
  zone.hasPendingMicrotasks = !!zone._countPendingMicrotasks;
}

export function onEnter(zone: NgZonePrivate) {
  zone._nesting++;
  if (zone.isStable) {
    zone.isStable = false;
    zone.onUnstable.emit(null);
  }
}

export function onLeave(zone: NgZonePrivate) {
  zone._nesting--;
  checkStable(zone);
}
